/* eslint-disable react/destructuring-assignment */
import useStyles from "./styles"
import SideFilter from "./components/SideFilter"
// eslint-disable-next-line import/no-cycle
import { PageHandler, Products } from "../.."
import usePage from "../../../redux/hook/pageHook"
import { useIsSearchQueryEmpty, useSearchQuery } from "../../../redux/hook/searchHooks"
import { setSearchQuery } from "../../../redux/searchReducer"
import { sendGAEventProductSearchViaLink } from "../../../lib/helper/googleAnalyticsHelper"
import { tabletBreakpoint } from "../../../lib/helper/viewportHelper"
import { useLanguage } from "../../../lib/hooks/languageHooks"
import { useUrlQuery, useUrlSearchQuery } from "../../../lib/hooks/urlQueryHooks"
import { useGetReplacementProductsQuery } from "../../../redux/services/broditWebApi"
import NoIndexMetaData from "../../MetaData/NoIndexMetadata"
import FilterProductToolbar from "../FilterProductToolbar/FilterProductToolbar"
import { useGetProductsByFilterQuery } from "../../../redux/services/productInfoApi"
import { findObjectById, isProductIdRegex } from "../../../lib/helper/commonHelper"
import { isSearchQueryEmpty } from "../../../lib/helper/searchHelper"
import { useLoadPrices } from "../../../redux/hook/priceHooks"
import { useDispatch } from "react-redux"
import {
    Box, useMediaQuery, useTheme
} from "@material-ui/core"
import React, {
    useEffect, useMemo, useState
} from "react"
import { useParams } from "react-router-dom"

const FilterProducts = () => {
    const dispatch = useDispatch()
    const classes = useStyles()
    const theme = useTheme()
    const page = usePage()
    const urlQuery = useUrlQuery()
    const language = useLanguage()
    const isSmallScreen = useMediaQuery(theme.breakpoints.down(tabletBreakpoint))
    let {
        brand: urlSelectedBrand,
        model: urlSelectedModel,
        collections: urlSelectedColletion
    } = useParams()

    const fetchCollectionStructure = async () => {
        const response = await fetch(`/api/Collections/GetCollectionHierarchy?language=${language}`)
        return response.json()
    }

    urlSelectedBrand = urlSelectedBrand && decodeURIComponent(urlSelectedBrand)
    urlSelectedModel = urlSelectedModel && decodeURIComponent(urlSelectedModel)
    urlSelectedColletion = urlSelectedColletion && decodeURIComponent(urlSelectedColletion)

    const [isUserShowingFilter, setIsUserShowingFilter] = useState(true)
    const [showFilter] = useState(true)
    const urlSearchQuery = useUrlSearchQuery(urlQuery)
    const isUrlSearchQueryEmpty = useIsSearchQueryEmpty(urlSearchQuery)
    const allSearchQueriesCombined = useSearchQuery()
    const isAllSearchQueryEmpty = useIsSearchQueryEmpty(allSearchQueriesCombined)
    const pagination = useMemo(() => (
        { offset: page.offset, limit: page.limit }
    ), [page.offset, page.limit])
    const getProductsByFilterRequest = useGetProductsByFilterQuery({
        filters: allSearchQueriesCombined, pagination, language
    })
    const {
        data: productsResult, isFetching, startedTimeStamp, error
    } = getProductsByFilterRequest
    const [forcedLoadingSpinner, setForcedLoadingSpinner] = useState(false)
    const loading = useMemo(
        () => !isAllSearchQueryEmpty && (forcedLoadingSpinner || isFetching),
        [isFetching, forcedLoadingSpinner, isAllSearchQueryEmpty]
    )
    const products = (productsResult?.products && !isAllSearchQueryEmpty && !error)
        ? productsResult?.products
        : []

    let productTotalCount = (productsResult?.pagedList?.response?.pagingHeader?.totalItems) ?? 0
    productTotalCount = (isAllSearchQueryEmpty || error) ? 0 : productTotalCount
    const showReplacementProds = (
        allSearchQueriesCombined?.texts?.length === 1
        && isProductIdRegex.test(allSearchQueriesCombined?.texts?.[0])
    )
    const { data: replacementProds } = useGetReplacementProductsQuery(
        allSearchQueriesCombined?.texts?.[0],
        { skip: !showReplacementProds }
    )

    const getUrl = (collection = null) => {
        let cleanedUrl = window.location.toString().split(/(?<=filter|brand|collections)/)[0]
        const isSingleBrand = allSearchQueriesCombined?.brands?.length === 1
        const isSingleModel = allSearchQueriesCombined?.models?.length === 1
        const isSingleCollection = allSearchQueriesCombined?.collections?.length === 1
        if (isSingleBrand) {
            cleanedUrl += `/brand/${encodeURIComponent(allSearchQueriesCombined?.brands[0])}`
        }
        if (isSingleBrand && isSingleModel) {
            cleanedUrl += `/${encodeURIComponent(allSearchQueriesCombined?.models[0])}`
        }
        if (isSingleCollection) {
            cleanedUrl += `/collection/${encodeURIComponent(allSearchQueriesCombined?.collections[0])}/${collection?.name || ""}`
        }
        const url = new URL(cleanedUrl)
        Object.keys(allSearchQueriesCombined).map(key => (
            url.searchParams.set(key, allSearchQueriesCombined[key])
        ))
        if (isSingleBrand) url.searchParams.delete("brands")
        if ((isSingleBrand && isSingleModel)) url.searchParams.delete("models")
        if (isSingleCollection) url.searchParams.delete("collections")
        url.searchParams.sort()
        return url
    }

    const setInitialUrl = () => {
        const url = getUrl()
        window.history.pushState({}, "", url)
    }

    const updateUrlParams = collection => {
        const url = getUrl(collection)
        window.history.replaceState({}, "", url)
    }

    const toggleFilter = () => {
        setIsUserShowingFilter(prev => !prev)
    }

    useLoadPrices(products.map(product => product.originalId))

    useEffect(() => {
        setForcedLoadingSpinner(true)
        const a = setTimeout(() => {
            setForcedLoadingSpinner(false)
        }, 100)
        return () => clearTimeout(a)
    }, [startedTimeStamp])

    useEffect(() => {
        // Hides aside filter when switching to mobile or tablet
        // Show filter when back to desktop
        setIsUserShowingFilter(!isSmallScreen)
    }, [isSmallScreen])

    useEffect(() => {
        const combinedSearchQuery = {
            ...!isUrlSearchQueryEmpty && urlSearchQuery,
            ...urlSelectedBrand && { brands: [urlSelectedBrand] },
            ...urlSelectedModel && { models: [urlSelectedModel] },
            ...urlSelectedColletion && { collections: [urlSelectedColletion] }
        }
        if (!isSearchQueryEmpty(combinedSearchQuery)) {
            if (combinedSearchQuery?.collections !== undefined && !combinedSearchQuery?.collections?.[0].includes("s")) {
                combinedSearchQuery.collections[0] = `s${combinedSearchQuery.collections[0]}`
            }
            // Update the search query with the url search parameters
            dispatch(
                setSearchQuery({
                    type: "filterProducts",
                    value: combinedSearchQuery,
                    clearFilter: true
                })
            )
            sendGAEventProductSearchViaLink()
        }

        setInitialUrl()

        return () => {
            dispatch(
                setSearchQuery({
                    type: "filterProducts",
                    value: {}
                })
            )
        }
    }, [])

    useEffect(() => {
        fetchCollectionStructure().then(result => {
            const res = findObjectById(result, allSearchQueriesCombined?.collections?.[0])
            updateUrlParams(res)
            window.scroll(0, 0)
        })
    }, [allSearchQueriesCombined, language])

    return (
        <main className={classes.content}>
            {
                (
                    !isAllSearchQueryEmpty
                    || !isUrlSearchQueryEmpty
                )
                && <NoIndexMetaData />
            }
            <FilterProductToolbar
                toggleFilter={toggleFilter}
                productTotalCount={productTotalCount}
                className={classes.flexGrow1}
                sx={{
                    marginLeft: {
                        xs: "0", sm: "0", md: "0", lg: "300px"
                    }
                }}
            />
            <Box className={classes.productBox}>
                {
                    isUserShowingFilter && showFilter
                    && (
                        <SideFilter
                            productsResult={productsResult}
                        />
                    )
                }
                <Box className={classes.flexGrow1}>
                    {(!isUserShowingFilter || !isSmallScreen)
                        && (
                            <Box>
                                <Products
                                    products={products}
                                    isLoading={loading}
                                    replacementProducts={replacementProds?.replacementProducts}
                                    searchedId={allSearchQueriesCombined?.texts?.[0]}
                                    isDeleted={replacementProds?.deleted}
                                />
                                <PageHandler type="pagePagination" itemCount={productTotalCount} />
                            </Box>
                        )}
                </Box>
            </Box>
        </main>
    )
}

export default FilterProducts
